'use client';

import { createContext, useCallback, useState } from 'react';
import SuggestDocumentCreateModal, {
	SuggestDocumentCreateModalProps,
} from '../blocks/SuggestDocumentCreateModal';
import {
	DocumentSettingsModal,
	DocumentSettingsModalProps,
} from '../blocks/DocumentSettingsModal';

export const ModalContext = createContext<ModalContextProps | null>( null );

export type ModalContextProps = {
	addModal: ( modalType: ModalType, props: ModalTypeProps<ModalType> ) => void;
	removeModal: ( modalType: ModalType ) => void;
};

export enum ModalType {
	SuggestDocumentCreateModal = 'SuggestDocumentCreateModal',
	DocumentSettingsModal = 'DocumentSettingsModal',
}

type ModalTypeProps<T> = T extends ModalType.SuggestDocumentCreateModal
	? SuggestDocumentCreateModalProps
	: T extends ModalType.DocumentSettingsModal
	? DocumentSettingsModalProps
	: never;

type ModalState = {
	[K in ModalType]?: ModalTypeProps<K>;
};

export const ModalContextProvider = ( {
	children,
}: {
	children: React.ReactNode;
} ) => {
	const [ modal, setModal ] = useState<ModalState>( {} );

	const addModal = useCallback(
		( modalType: ModalType, props: ModalTypeProps<ModalType> ) => {
			setModal( ( prev ) => ( { ...prev, [ modalType ]: props } ) );
		},
		[]
	);

	const removeModal = useCallback( ( modalType: ModalType ) => {
		setModal( ( prev ) =>
			Object.fromEntries(
				Object.entries( prev ).filter( ( [ key ] ) => key !== modalType )
			)
		);
	}, [] );

	return (
		<ModalContext.Provider value={ { addModal, removeModal } }>
			{ children }
			{ modal[ ModalType.SuggestDocumentCreateModal ] ? (
				<SuggestDocumentCreateModal
					{ ...modal[ ModalType.SuggestDocumentCreateModal ] }
					onClose={ () => {
						modal[ ModalType.SuggestDocumentCreateModal ]?.onClose?.();
						removeModal( ModalType.SuggestDocumentCreateModal );
					} }
				/>
			) : null }
			{ modal[ ModalType.DocumentSettingsModal ] ? (
				<DocumentSettingsModal
					{ ...modal[ ModalType.DocumentSettingsModal ] }
					onClose={ () => {
						modal[ ModalType.DocumentSettingsModal ]?.onClose?.();
						removeModal( ModalType.DocumentSettingsModal );
					} }
				/>
			) : null }
		</ModalContext.Provider>
	);
};
